import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { BarChart3, Building2, CreditCard, Home, LayoutDashboard, Linkedin, Megaphone, Network, Radar, Search, Send, Store, Users, Workflow } from "lucide-react";
import { clientAllowed, clientHome, clientMods, hasRadar } from "@/lib/clients";
import { cn } from "@/lib/utils";

// Client navigation: product switcher (Revenue Command Center / Sourcing Intelligence) + that product's sub-menu.
const RCC = ["/admin/rcc/radar", "Radar", [
  ["/admin/rcc/radar", "Companies", Building2, true], ["/admin/rcc/radar/people", "Decision makers", Users], ["/admin/rcc/workflows", "Workflows", Workflow],
  ["/admin/rcc/radar/campaigns", "Email Campaigns", Megaphone], ["/admin/rcc/radar/mpc", "MPC Marketplace", Store], ["/admin/rcc/radar/linkedin", "LinkedIn Follow-Ups", Linkedin],
  ["/admin/rcc/radar/intel", "Market Intelligence", BarChart3], ["/admin/rcc/radar/connections", "My Connections", Network]]];
const SIP = ["/admin/si", "Sourcing", [
  ["/admin/si", "Projects", LayoutDashboard, true], ["/admin/si/quick", "Quick people search", Search], ["/admin/si/talent", "My Talent & Connections", Network]]];

export const useClientNav = (client) => {
  const m = clientMods(client);
  const products = [
    (hasRadar(client) || m.workflows !== false) && { k: "rcc", label: "Revenue Command Center", short: "Revenue", Icon: Radar, accent: "from-[#D4AF37] to-[#F3C649]", match: "/admin/rcc", subs: RCC[2] },
    m.si && { k: "si", label: "Sourcing Intelligence", short: "Sourcing", Icon: Users, accent: "from-[#8224e3] to-[#c79bff]", match: "/admin/si", subs: SIP[2] },
  ].filter(Boolean).map((p) => ({ ...p, subs: p.subs.filter(([to]) => clientAllowed(client, to)) })).filter((p) => p.subs.length);
  return products;
};

const isOn = (path, to, exact) => (exact ? path === to || path === `${to}/` : path === to || path.startsWith(`${to}/`));

export const ProductSwitch = ({ client }) => {
  const { pathname } = useLocation();
  const products = useClientNav(client);
  const home = clientHome(client) === "/client/home";
  return (
    <nav className="flex min-w-0 items-center gap-1 rounded-full border border-[#27272A] bg-[#0f0f12]/80 p-1" data-testid="client-nav">
      {home && <NavLink to="/client/home" title="Home" className={cn("inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors", pathname.startsWith("/client/home") ? "bg-zinc-100 text-black" : "text-zinc-400 hover:bg-[#18181B] hover:text-zinc-100")} data-testid="client-nav-home"><Home className="h-4 w-4" /></NavLink>}
      {products.map((p) => {
        const on = pathname.startsWith(p.match);
        return (
          <NavLink key={p.k} to={p.subs[0][0]} className={cn("group relative inline-flex shrink-0 items-center gap-2 overflow-hidden rounded-full px-3.5 py-1.5 text-xs font-medium transition-[color,box-shadow]", on ? "text-black shadow-[0_0_18px_rgba(212,175,55,0.25)]" : "text-zinc-300 hover:text-white")} data-testid={`client-product-${p.k}`}>
            <span className={cn("absolute inset-0 bg-gradient-to-r transition-opacity duration-300", p.accent, on ? "opacity-100" : "opacity-0 group-hover:opacity-15")} />
            <p.Icon className="relative h-3.5 w-3.5" /><span className="relative hidden lg:inline">{p.label}</span><span className="relative lg:hidden">{p.short}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

export const Utilities = ({ client, can }) => {
  const { pathname } = useLocation();
  const items = [["/client/outreach", "Outreach", Send], can && ["/client/billing", "Billing", CreditCard]].filter(Boolean);
  return items.map(([to, l, I]) => <NavLink key={to} to={to} title={l} aria-label={l} className={cn("inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-xs transition-colors", pathname.startsWith(to) ? "bg-[#18181B] text-[#F3C649] ring-1 ring-[#D4AF37]/40" : "text-zinc-400 hover:bg-[#18181B] hover:text-zinc-100")} data-testid={`client-nav-${l.toLowerCase()}`}><I className="h-3.5 w-3.5" /><span className="hidden sm:inline">{l}</span></NavLink>);
};

export const SubNav = ({ client }) => {
  const { pathname } = useLocation();
  const p = useClientNav(client).find((x) => pathname.startsWith(x.match));
  const row = useRef(null);
  useEffect(() => { row.current?.querySelector("[data-active=true]")?.scrollIntoView({ inline: "center", block: "nearest" }); }, [pathname]);
  if (!p || p.subs.length < 2) return null;
  const active = [...p.subs].sort((a, b) => b[0].length - a[0].length).find(([to, , , exact]) => isOn(pathname, to, exact) || (!exact && pathname.startsWith(to)))?.[0]
    || (p.k === "rcc" && pathname.startsWith("/admin/rcc/radar/companies") ? "/admin/rcc/radar" : p.k === "si" && /^\/admin\/si\/(p\/|new|settings)/.test(pathname) ? "/admin/si" : null);
  return (
    <div className="border-t border-[#1f1f23] bg-[#09090B]/70" data-testid={`client-subnav-${p.k}`}>
      <div className="relative mx-auto max-w-[1400px]">
        <div key={p.k} ref={row} className="flex gap-1 overflow-x-auto px-4 py-2 [scrollbar-width:none] md:px-8 [&::-webkit-scrollbar]:hidden">
          <span className={cn("mr-2 hidden shrink-0 items-center bg-gradient-to-r bg-clip-text font-mono text-[10px] uppercase tracking-[0.2em] text-transparent md:inline-flex", p.accent)}>{p.label}</span>
          {p.subs.map(([to, l, I]) => {
            const on = active === to;
            return <NavLink key={to} to={to} data-active={on} className={cn("relative inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs transition-colors", on ? "bg-[#18181B] text-zinc-50" : "text-zinc-400 hover:bg-[#121215] hover:text-zinc-100")} data-testid={`client-sub-${to.split("/").filter(Boolean).slice(-1)[0]}`}>
              <I className={cn("h-3.5 w-3.5", on ? (p.k === "si" ? "text-[#c79bff]" : "text-[#F3C649]") : "")} />{l}
              {on && <span className={cn("absolute inset-x-2 -bottom-2 h-0.5 rounded-full bg-gradient-to-r", p.accent)} />}
            </NavLink>;
          })}
        </div>
        <span className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#09090B] to-transparent md:hidden" />
      </div>
    </div>
  );
};
